class TimephoriaBundleBuilder extends HTMLElement {
  constructor() {
    super();
    this.tabs = [];
    this.tiers = [];
    this.strings = {};
    this.swatches = {};
    this.activeTab = 'all';
    this.selected = [];
    this.cardState = new Map();
    this.adding = false;
    this.modalProduct = null;
    this.modalVariant = null;
    this.modalImageIndex = 0;
    this.handleModalKeydown = (event) => {
      if (event.key === 'Escape') {
        this.closeProductModal();
        this.closeHelpModal();
      }
      if (event.key === 'ArrowLeft') this.moveModalImage(-1);
      if (event.key === 'ArrowRight') this.moveModalImage(1);
    };
  }

  connectedCallback() {
    this.tabs = this.readJson('[data-tabs-json]', []).filter((tab) => (
      tab
      && tab.id
      && tab.label
      && Array.isArray(tab.products)
      && tab.products.length
    ));
    if (!this.tabs.some((tab) => tab.id === this.activeTab)) {
      this.activeTab = this.tabs[0]?.id || '';
    }
    this.tiers = this.readJson('[data-tiers-json]', [])
      .map((tier) => {
        const baseThresholdCents = this.parseMoneyToCents(tier.amount);
        return {
          ...tier,
          baseThresholdCents,
          thresholdCents: this.convertBaseCentsToCurrentCents(baseThresholdCents),
        };
      })
      .filter((tier) => tier.thresholdCents > 0);
    this.tiers.sort((a, b) => a.thresholdCents - b.thresholdCents);
    this.strings = this.readJson('[data-strings-json]', {});
    this.swatches = this.parseSwatches(this.dataset.swatchList || '');

    this.tabsEl = this.querySelector('.tp-byob__tabs');
    this.productsEl = this.querySelector('.tp-byob__products');
    this.summaryEl = this.querySelector('.tp-byob__summary');
    this.progressEl = this.querySelector('[data-progress]');
    this.selectedWrapEl = this.querySelector('[data-selected-wrap]');
    this.selectedEl = this.querySelector('[data-selected]');
    this.emptySlotsEl = this.querySelector('[data-empty-slots]');
    this.limitMessageEl = this.querySelector('[data-limit-message]');
    this.subtotalEl = this.querySelector('[data-subtotal]');
    this.addBundleButton = this.querySelector('.tp-byob__add-bundle');
    this.toggleButton = this.querySelector('.tp-byob__summary-toggle');
    this.toggleText = this.querySelector('.tp-byob__summary-toggle-text');
    this.scrollIndicator = this.querySelector('.tp-byob__scroll-indicator span');
    this.selectedPrevButton = this.querySelector('[data-selected-prev]');
    this.selectedNextButton = this.querySelector('[data-selected-next]');
    this.helpModalEl = this.querySelector('[data-help-modal]');
    this.helpOpenButton = this.querySelector('[data-help-open]');
    this.modalEl = this.createProductModal();
    this.appendChild(this.modalEl);

    this.restoreSelectedFromStorage();
    this.applyPrefillFromUrl();
    this.renderTabs();
    this.renderProducts();
    this.renderProgress();
    this.renderSummary();
    this.bindEvents();
    this.setupMobileSummary();
  }

  readJson(selector, fallback) {
    try {
      const el = this.querySelector(selector);
      return el ? JSON.parse(el.textContent) : fallback;
    } catch (error) {
      console.error('Bundle JSON parse error:', error);
      return fallback;
    }
  }

  createProductModal() {
    const modal = document.createElement('div');
    modal.className = 'tp-byob-modal';
    modal.hidden = true;
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', 'Product details');
    return modal;
  }

  openHelpModal() {
    if (!this.helpModalEl) return;
    this.helpModalEl.hidden = false;
    document.documentElement.classList.add('tp-byob-modal-open');
    document.addEventListener('keydown', this.handleModalKeydown);
    this.helpModalEl.querySelector('[data-help-close]')?.focus();
  }

  closeHelpModal() {
    if (!this.helpModalEl || this.helpModalEl.hidden) return;
    this.helpModalEl.hidden = true;
    document.documentElement.classList.remove('tp-byob-modal-open');
    document.removeEventListener('keydown', this.handleModalKeydown);
  }

  get storageKey() {
    return 'timephoria-byob-selected';
  }

  buildSelectedItem(product, variant) {
    return {
      uid: `${variant.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      productId: product.id,
      productTitle: product.title,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      image: variant.image || product.image,
    };
  }

  restoreSelectedFromStorage() {
    try {
      const stored = JSON.parse(window.sessionStorage.getItem(this.storageKey) || '[]');
      if (!Array.isArray(stored) || !stored.length) return;

      this.selected = stored
        .map((variantId) => {
          const match = this.findProductByVariantId(Number(variantId));
          if (!match || !match.variant.available) return null;
          return this.buildSelectedItem(match.product, match.variant);
        })
        .filter(Boolean);
    } catch (error) {
      console.error('Bundle storage restore error:', error);
    }
  }

  saveSelectedToStorage() {
    try {
      window.sessionStorage.setItem(
        this.storageKey,
        JSON.stringify(this.selected.map((item) => item.variantId))
      );
    } catch (error) {
      console.error('Bundle storage save error:', error);
    }
  }

  clearSelectedStorage() {
    try {
      window.sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      console.error('Bundle storage clear error:', error);
    }
  }

  applyPrefillFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const variantId = Number(params.get('byob_variant'));
    if (!variantId) return;

    const match = this.findProductByVariantId(variantId);
    if (!match || !match.variant.available) return;

    this.activeTab = match.tab.id;
    this.cardState.set(String(match.product.id), match.variant);
    this.addVariantToSelection(match.product, match.variant);

    params.delete('byob_variant');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }

  parseSwatches(raw) {
    return raw.split('\n').reduce((map, line) => {
      const parts = line.split(':');
      if (parts.length >= 2) {
        const name = parts.shift().trim().toLowerCase();
        const value = parts.join(':').trim();
        if (name && value) map[name] = value;
      }
      return map;
    }, {});
  }

  bindEvents() {
    this.tabsEl.addEventListener('click', (event) => {
      const button = event.target.closest('[data-tab]');
      if (!button) return;
      this.activeTab = button.dataset.tab;
      this.renderTabs();
      this.renderProducts();
    });

    this.productsEl.addEventListener('click', (event) => {
      const modalTrigger = event.target.closest('[data-open-product]');
      if (modalTrigger) {
        event.preventDefault();
        this.openProductModal(modalTrigger.closest('[data-product-id]'));
        return;
      }

      const swatch = event.target.closest('[data-swatch-variant]');
      if (swatch) {
        this.selectCardVariant(swatch.closest('[data-product-id]'), Number(swatch.dataset.swatchVariant));
        return;
      }

      const addButton = event.target.closest('[data-add-product]');
      if (addButton) {
        this.addProduct(addButton.closest('[data-product-id]'), addButton);
      }
    });

    this.productsEl.addEventListener('mousedown', (event) => {
      if (event.target.closest('.tp-byob-card__title')) {
        event.stopPropagation();
      }
    });

    this.selectedEl.addEventListener('click', (event) => {
      const removeButton = event.target.closest('[data-remove-selected]');
      if (!removeButton) return;
      this.selected = this.selected.filter((item) => item.uid !== removeButton.dataset.removeSelected);
      this.renderSummary();
    });

    this.selectedEl.addEventListener('scroll', () => this.updateScrollIndicator(), { passive: true });

    this.selectedPrevButton?.addEventListener('click', () => this.scrollSelectedItems(-1));
    this.selectedNextButton?.addEventListener('click', () => this.scrollSelectedItems(1));

    this.toggleButton.addEventListener('click', () => {
      const expanded = !this.summaryEl.classList.contains('is-expanded');
      this.setSummaryExpanded(expanded);
      this.updateScrollIndicator();
    });

    this.addBundleButton.addEventListener('click', () => this.addBundleToCart());

    this.modalEl.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-modal-close]');
      if (closeTrigger || event.target === this.modalEl) {
        this.closeProductModal();
        return;
      }

      const swatch = event.target.closest('[data-modal-swatch-variant]');
      if (swatch) {
        this.selectModalVariant(Number(swatch.dataset.modalSwatchVariant));
        return;
      }

      const galleryButton = event.target.closest('[data-modal-gallery]');
      if (galleryButton) {
        this.moveModalImage(Number(galleryButton.dataset.modalGallery));
        return;
      }

      const imageDot = event.target.closest('[data-modal-image-index]');
      if (imageDot) {
        this.selectModalImage(Number(imageDot.dataset.modalImageIndex));
        return;
      }

      const addButton = event.target.closest('[data-modal-add-product]');
      if (addButton) {
        this.addModalProduct(addButton);
      }
    });

    this.modalEl.addEventListener('scroll', (event) => {
      if (event.target.matches('[data-modal-image-track]')) {
        this.updateModalImageFromScroll(event.target);
      }
    }, true);

    this.helpOpenButton?.addEventListener('click', () => this.openHelpModal());

    this.helpModalEl?.addEventListener('click', (event) => {
      const closeTrigger = event.target.closest('[data-help-close]');
      if (closeTrigger || event.target === this.helpModalEl) {
        this.closeHelpModal();
      }
    });
  }

  setupMobileSummary() {
    const update = () => {
      if (!window.matchMedia('(max-width: 767px)').matches) {
        this.summaryEl.classList.remove('is-static-mobile');
        this.style.removeProperty('--mobile-summary-height');
        return;
      }

      const rect = this.getBoundingClientRect();
      const summaryHeight = this.summaryEl.offsetHeight || 0;
      const shouldStop = rect.bottom <= window.innerHeight + summaryHeight * 0.35;
      this.summaryEl.classList.toggle('is-static-mobile', shouldStop);
      this.style.setProperty('--mobile-summary-height', shouldStop ? '0px' : `${summaryHeight}px`);
    };

    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    this.mobileSummaryUpdate = update;
  }

  setSummaryExpanded(expanded) {
    if (!this.summaryEl || !this.toggleButton || !this.toggleText) return;

    this.summaryEl.classList.toggle('is-expanded', expanded);
    this.toggleButton.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    this.toggleText.textContent = expanded ? '' : this.strings.expand;
  }

  get activeProducts() {
    return this.tabs.find((tab) => tab.id === this.activeTab)?.products || [];
  }

  get minItems() {
    return Number(this.dataset.minItems || 2);
  }

  get maxItems() {
    if (this.dataset.enableMax !== 'true') return null;
    return Number(this.dataset.maxItems || 30);
  }

  get tierDiscountsEnabled() {
    return this.dataset.enableTierDiscounts === 'true';
  }

  get quantityDiscountsEnabled() {
    const value = String(this.dataset.enableQuantityDiscounts ?? 'true').toLowerCase();
    return value !== 'false' && value !== '0' && value !== 'off';
  }

  get quantityDiscountPercent() {
    if (!this.quantityDiscountsEnabled) return 0;
    const count = this.selected.length;
    const tiers = [
      {
        count: Number(this.getAttribute('data-quantity-discount-1-count') || 0),
        percent: Number(this.getAttribute('data-quantity-discount-1-percent') || 0),
      },
      {
        count: Number(this.getAttribute('data-quantity-discount-2-count') || 0),
        percent: Number(this.getAttribute('data-quantity-discount-2-percent') || 0),
      },
    ].filter((tier) => tier.count > 0 && tier.percent > 0)
      .sort((a, b) => b.count - a.count);

    const matched = tiers.find((tier) => count >= tier.count);
    return matched ? matched.percent : 0;
  }

  get selectedSubtotal() {
    return this.selected.reduce((sum, item) => sum + Number(item.price || 0), 0);
  }

  get currentTier() {
    const total = this.selectedSubtotal;
    return [...this.tiers].reverse().find((tier) => total >= tier.thresholdCents) || null;
  }

  get nextTier() {
    const total = this.selectedSubtotal;
    return this.tiers.find((tier) => total < tier.thresholdCents) || null;
  }

  get discountPercent() {
    const tierDiscount = this.tierDiscountsEnabled && this.currentTier?.discountEnabled
      ? Number(this.currentTier?.discount || 0)
      : 0;
    return Math.max(tierDiscount, this.quantityDiscountPercent);
  }

  get currencyRate() {
    const rate = Number(window.Shopify?.currency?.rate || 1);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
  }

  get currencyCode() {
    return window.Shopify?.currency?.active || window.Shopify?.currency?.currency || '';
  }

  convertBaseCentsToCurrentCents(cents) {
    return Math.round(Number(cents || 0) * this.currencyRate);
  }

  formatMoney(cents) {
    if (window.FoxTheme?.Currency?.formatMoney) {
      return window.FoxTheme.Currency.formatMoney(cents, this.dataset.moneyFormat || window.FoxTheme.settings?.moneyFormat);
    }
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents);
    return `$${(Number(cents || 0) / 100).toFixed(2)}`;
  }

  formatGoalMoney(cents) {
    const formatted = this.formatMoney(cents);
    const code = this.currencyCode;
    if (!code || formatted.includes(code)) return formatted;
    return `${formatted} ${code}`;
  }

  parseMoneyToCents(value) {
    const normalized = String(value ?? '').replace(/[^0-9.,]/g, '').replace(',', '.');
    const amount = Number(normalized);
    return Number.isFinite(amount) && amount > 0 ? Math.round(amount * 100) : 0;
  }

  renderTabs() {
    this.tabsEl.innerHTML = this.tabs.map((tab) => `
      <button class="tp-byob__tab ${tab.id === this.activeTab ? 'is-active' : ''}" type="button" data-tab="${this.escape(tab.id)}">
        ${this.escape(tab.label)}
      </button>
    `).join('');
  }

  renderProducts() {
    const products = this.activeProducts;
    this.productsEl.innerHTML = products.map((product) => this.renderProductCard(product)).join('');
  }

  renderProductCard(product) {
    const availableVariants = (product.variants || []).filter((variant) => variant.available);
    const selectedVariant = this.cardState.get(String(product.id)) || availableVariants[0] || product.variants?.[0];
    if (selectedVariant) this.cardState.set(String(product.id), selectedVariant);
    const image = selectedVariant?.image || product.image || '';
    const isAvailable = Boolean(selectedVariant?.available);
    const buttonLabel = isAvailable ? this.strings.add : this.strings.soldOut;
    const productUrl = this.getProductUrl(product, selectedVariant);

    return `
      <article class="tp-byob-card" role="listitem" data-product-id="${product.id}">
        <a class="tp-byob-card__image-link" href="${this.escape(productUrl)}" data-open-product aria-label="${this.escape(product.title)}">
          <div class="tp-byob-card__image-wrap">
            ${image ? `<img class="tp-byob-card__image" src="${this.escape(image)}" alt="${this.escape(product.title)}" loading="lazy" width="600" height="600">` : ''}
          </div>
        </a>
        <div class="tp-byob-card__content">
          <div class="tp-byob-card__swatches" aria-label="${this.escape(product.title)} shades">
            ${(product.variants || []).map((variant) => this.renderSwatch(variant, selectedVariant)).join('')}
          </div>
          <textarea class="tp-byob-card__title" readonly rows="2" data-open-product aria-label="${this.escape(product.title)}">${this.escape(product.title)}</textarea>
          <p class="tp-byob-card__price">${this.formatGoalMoney(selectedVariant?.price || 0)}</p>
        </div>
        <button class="tp-byob-card__button" type="button" data-add-product ${isAvailable ? '' : 'disabled'}>
          ${this.escape(buttonLabel)}
        </button>
      </article>
    `;
  }

  renderSwatch(variant, selectedVariant, attribute = 'data-swatch-variant') {
    const label = variant.option1 || variant.title;
    const swatch = this.swatches[String(label).toLowerCase()];
    const style = swatch
      ? `--swatch:${this.escape(swatch)}`
      : variant.image
        ? `--swatch:#fff;background-image:url(${this.escape(variant.image)});background-size:cover;background-position:center;`
        : '--swatch:#fff';
    return `
      <button
        class="tp-byob-card__swatch ${selectedVariant?.id === variant.id ? 'is-active' : ''}"
        type="button"
        ${attribute}="${variant.id}"
        style="${style}"
        ${variant.available ? '' : 'disabled'}
        aria-label="${this.escape(label)}"
      ></button>
    `;
  }

  getProductUrl(product, variant) {
    if (!product?.url) return '#';
    return `${product.url}${variant?.id ? `${product.url.includes('?') ? '&' : '?'}variant=${variant.id}` : ''}`;
  }

  getVariantImages(product, variant) {
    const images = Array.isArray(variant?.images) ? variant.images.filter(Boolean) : [];
    if (images.length) return images;
    if (variant?.image) return [variant.image];
    if (product?.image) return [product.image];
    return [];
  }

  openProductModal(card) {
    const product = this.findProduct(Number(card?.dataset?.productId));
    if (!product) return;

    const availableVariants = (product.variants || []).filter((variant) => variant.available);
    const selectedVariant = this.cardState.get(String(product.id)) || availableVariants[0] || product.variants?.[0];
    if (!selectedVariant) return;

    this.modalProduct = product;
    this.modalVariant = selectedVariant;
    this.modalImageIndex = 0;
    this.cardState.set(String(product.id), selectedVariant);
    this.renderProductModal();
    this.modalEl.hidden = false;
    document.documentElement.classList.add('tp-byob-modal-open');
    document.addEventListener('keydown', this.handleModalKeydown);
    this.modalEl.querySelector('[data-modal-close]')?.focus();
  }

  closeProductModal() {
    if (!this.modalEl || this.modalEl.hidden) return;
    this.modalEl.hidden = true;
    this.modalEl.innerHTML = '';
    this.modalProduct = null;
    this.modalVariant = null;
    this.modalImageIndex = 0;
    document.documentElement.classList.remove('tp-byob-modal-open');
    document.removeEventListener('keydown', this.handleModalKeydown);
  }

  renderProductModal() {
    const product = this.modalProduct;
    const selectedVariant = this.modalVariant;
    if (!product || !selectedVariant) return;

    const images = this.getVariantImages(product, selectedVariant);
    const imageIndex = Math.min(Math.max(this.modalImageIndex, 0), Math.max(images.length - 1, 0));
    this.modalImageIndex = imageIndex;
    const image = images[imageIndex] || '';
    const label = selectedVariant.option1 || selectedVariant.title || '';
    const isAvailable = Boolean(selectedVariant.available);
    const description = String(product.description || '').trim();
    const productUrl = this.getProductUrl(product, selectedVariant);

    this.modalEl.innerHTML = `
      <div class="tp-byob-modal__dialog">
        <button class="tp-byob-modal__close" type="button" data-modal-close aria-label="Close">&times;</button>
        <div class="tp-byob-modal__media">
          <div class="tp-byob-modal__image-wrap" data-modal-image-track>
            ${images.map((item, index) => `
              <img
                class="tp-byob-modal__image ${index === imageIndex ? 'is-active' : ''}"
                src="${this.escape(item)}"
                alt="${this.escape(product.title)}"
                width="700"
                height="700"
                data-modal-slide="${index}"
              >
            `).join('')}
            ${images.length > 1 ? `
              <button class="tp-byob-modal__nav tp-byob-modal__nav--prev" type="button" data-modal-gallery="-1" aria-label="Previous image"></button>
              <button class="tp-byob-modal__nav tp-byob-modal__nav--next" type="button" data-modal-gallery="1" aria-label="Next image"></button>
            ` : ''}
          </div>
          ${images.length > 1 ? `
            <div class="tp-byob-modal__dots" aria-label="Product images">
              ${images.map((_, index) => `
                <button
                  class="tp-byob-modal__dot ${index === imageIndex ? 'is-active' : ''}"
                  type="button"
                  data-modal-image-index="${index}"
                  aria-label="Show image ${index + 1}"
                ></button>
              `).join('')}
            </div>
          ` : ''}
        </div>
        <div class="tp-byob-modal__info">
          <h3 class="tp-byob-modal__title">${this.escape(product.title)}</h3>
          ${description ? `<p class="tp-byob-modal__description">${this.escape(description)}</p>` : ''}
          <p class="tp-byob-modal__price">${this.formatGoalMoney(selectedVariant.price || 0)}</p>
          ${label ? `<p class="tp-byob-modal__variant">Color: ${this.escape(label)}</p>` : ''}
          <div class="tp-byob-modal__swatches" aria-label="${this.escape(product.title)} shades">
            ${(product.variants || []).map((variant) => this.renderSwatch(variant, selectedVariant, 'data-modal-swatch-variant')).join('')}
          </div>
          <button class="tp-byob-modal__add" type="button" data-modal-add-product ${isAvailable ? '' : 'disabled'}>
            ${this.escape(isAvailable ? 'Add to bundle' : (this.strings.soldOut || 'Sold out'))}
          </button>
          <p class="tp-byob-modal__message" data-modal-message hidden></p>
          <a class="tp-byob-modal__details" href="${this.escape(productUrl)}">View product details</a>
        </div>
      </div>
    `;
    this.syncModalImageTrack();
  }

  moveModalImage(direction) {
    if (!this.modalProduct || !this.modalVariant || this.modalEl.hidden) return;
    const images = this.getVariantImages(this.modalProduct, this.modalVariant);
    if (images.length < 2) return;
    this.modalImageIndex = (this.modalImageIndex + direction + images.length) % images.length;
    this.renderProductModal();
  }

  selectModalImage(index) {
    if (!this.modalProduct || !this.modalVariant || this.modalEl.hidden) return;
    const images = this.getVariantImages(this.modalProduct, this.modalVariant);
    if (!images.length || index < 0 || index >= images.length) return;
    this.modalImageIndex = index;
    this.renderProductModal();
  }

  syncModalImageTrack() {
    const track = this.modalEl.querySelector('[data-modal-image-track]');
    if (!track) return;
    window.requestAnimationFrame(() => {
      track.scrollLeft = track.clientWidth * this.modalImageIndex;
    });
  }

  updateModalImageFromScroll(track) {
    if (!track || !track.clientWidth) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    if (index === this.modalImageIndex) return;
    this.modalImageIndex = index;
    this.modalEl.querySelectorAll('[data-modal-image-index]').forEach((dot) => {
      dot.classList.toggle('is-active', Number(dot.dataset.modalImageIndex) === index);
    });
  }

  selectModalVariant(variantId) {
    const product = this.modalProduct;
    const variant = product?.variants?.find((item) => item.id === variantId);
    if (!product || !variant || !variant.available) return;

    this.modalVariant = variant;
    this.modalImageIndex = 0;
    this.cardState.set(String(product.id), variant);
    const card = this.productsEl.querySelector(`[data-product-id="${product.id}"]`);
    if (card) card.outerHTML = this.renderProductCard(product);
    this.renderProductModal();
  }

  addModalProduct(button) {
    if (!button || button.disabled) return;
    const maxItems = this.maxItems;
    const messageEl = this.modalEl.querySelector('[data-modal-message]');
    if (maxItems && this.selected.length >= maxItems) {
      const message = this.dataset.maxMessage || '';
      this.limitMessageEl.textContent = message;
      this.limitMessageEl.hidden = false;
      if (messageEl) {
        messageEl.textContent = message;
        messageEl.hidden = false;
      }
      return;
    }

    if (!this.modalProduct || !this.modalVariant || !this.modalVariant.available) return;
    this.addVariantToSelection(this.modalProduct, this.modalVariant);
    button.disabled = true;
    button.classList.add('is-added');
    button.textContent = this.strings.added || 'Added';
    if (messageEl) messageEl.hidden = true;
    window.setTimeout(() => {
      if (!this.modalEl.hidden) {
        button.classList.remove('is-added');
        button.classList.add('is-restoring');
        button.textContent = 'Add to bundle';
        button.disabled = false;
        window.setTimeout(() => {
          button.classList.remove('is-restoring');
        }, 260);
      }
    }, 850);
    this.renderSummary();
  }

  selectCardVariant(card, variantId) {
    if (!card) return;
    const product = this.findProduct(Number(card.dataset.productId));
    const variant = product?.variants?.find((item) => item.id === variantId);
    if (!product || !variant || !variant.available) return;
    this.cardState.set(String(product.id), variant);
    card.outerHTML = this.renderProductCard(product);
  }

  addProduct(card, button) {
    if (!card || !button || button.disabled) return;
    const maxItems = this.maxItems;
    if (maxItems && this.selected.length >= maxItems) {
      this.limitMessageEl.textContent = this.dataset.maxMessage || '';
      this.limitMessageEl.hidden = false;
      return;
    }

    const product = this.findProduct(Number(card.dataset.productId));
    const variant = this.cardState.get(String(product?.id));
    if (!product || !variant || !variant.available) return;

    this.addVariantToSelection(product, variant);
    button.disabled = true;
    button.classList.add('is-added');
    button.textContent = this.strings.added;
    window.setTimeout(() => {
      button.classList.remove('is-added');
      button.classList.add('is-restoring');
      button.textContent = this.strings.add;
      button.disabled = false;
      window.setTimeout(() => {
        button.classList.remove('is-restoring');
      }, 260);
    }, 850);
    this.renderSummary();
  }

  addVariantToSelection(product, variant) {
    if (!product || !variant || !variant.available) return;
    const wasEmpty = this.selected.length === 0;
    this.selected.unshift(this.buildSelectedItem(product, variant));
    if (wasEmpty && window.matchMedia('(max-width: 767px)').matches) {
      this.setSummaryExpanded(true);
    }
  }

  findProduct(productId) {
    for (const tab of this.tabs) {
      const found = (tab.products || []).find((product) => product.id === productId);
      if (found) return found;
    }
    return null;
  }

  findProductByVariantId(variantId) {
    for (const tab of this.tabs) {
      for (const product of (tab.products || [])) {
        const variant = (product.variants || []).find((item) => Number(item.id) === Number(variantId));
        if (variant) return { tab, product, variant };
      }
    }
    return null;
  }

  renderProgress() {
    const total = this.selectedSubtotal;
    const tiers = this.tiers;

    if (!tiers.length) {
      this.querySelectorAll('.tp-byob__progress').forEach((el) => {
        el.innerHTML = '';
        el.hidden = true;
      });
      return;
    }

    const progress = this.getTierProgress(total, tiers);
    const next = this.nextTier;
    const current = this.currentTier;
    let message = '';

    if (next) {
      const needed = Math.max(0, next.thresholdCents - total);
      message = this.fillTierText(next.beforeText || `Add {amount} more to get ${next.label || 'the next offer'}`, next, needed);
    } else if (current) {
      message = this.fillTierText(current.reachedText || '', current, 0);
    } else {
      message = this.strings.minMessage?.replace('{count}', this.minItems) || '';
    }

    const markup = `
      <p class="tp-byob-progress__message">${this.escape(message)}</p>
      <div class="tp-byob-progress__rail" style="--progress:${progress}%">
        <span class="tp-byob-progress__fill"></span>
      </div>
      <div class="tp-byob-progress__steps" style="--tier-count:${Math.max(tiers.length, 1)}">
        ${tiers.map((tier) => {
          const reached = total >= tier.thresholdCents;
          return `
          <div class="tp-byob-progress__step ${reached ? 'is-reached' : ''}">
            <span class="tp-byob-progress__dot">${reached ? '✓' : ''}</span>
            <span class="tp-byob-progress__threshold">${this.escape(this.formatGoalMoney(tier.thresholdCents))}</span>
            <span class="tp-byob-progress__label">${this.escape(tier.label || '')}</span>
          </div>
        `;
        }).join('')}
      </div>
    `;

    this.querySelectorAll('.tp-byob__progress').forEach((el) => {
      el.hidden = false;
      el.innerHTML = markup;
    });
  }

  fillTierText(template, tier, neededCents) {
    return String(template || '')
      .replace(/\{amount\}/g, this.formatGoalMoney(neededCents))
      .replace(/\{count\}/g, this.formatGoalMoney(neededCents))
      .replace(/\{target\}/g, this.formatGoalMoney(tier.thresholdCents))
      .replace(/\{label\}/g, tier.label || '')
      .replace(/\{discount\}/g, `${this.tierDiscountsEnabled && tier.discountEnabled ? Number(tier.discount || 0) : 0}%`);
  }

  getTierProgress(total, tiers) {
    if (!tiers.length) return 0;
    if (tiers.length === 1) {
      const threshold = tiers[0].thresholdCents || 1;
      return total >= threshold ? 100 : Math.min(50, (total / threshold) * 50);
    }

    const positions = tiers.map((_, index) => ((index + 0.5) / tiers.length) * 100);

    if (total <= tiers[0].thresholdCents) {
      return Math.max(0, (total / tiers[0].thresholdCents) * positions[0]);
    }

    for (let index = 1; index < tiers.length; index += 1) {
      const previous = tiers[index - 1];
      const current = tiers[index];
      if (total <= current.thresholdCents) {
        const range = current.thresholdCents - previous.thresholdCents || 1;
        const ratio = (total - previous.thresholdCents) / range;
        return positions[index - 1] + ratio * (positions[index] - positions[index - 1]);
      }
    }

    return 100;
  }

  renderSummary() {
    this.renderProgress();
    this.saveSelectedToStorage();
    const total = this.selectedSubtotal;
    const discountPercent = this.discountPercent;
    const discount = Math.round(total * discountPercent / 100);
    const finalTotal = total - discount;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const shouldScroll = this.selected.length > (isMobile ? 2 : 3);

    this.selectedWrapEl.classList.add('has-items');
    this.selectedWrapEl.classList.toggle('is-scrollable', shouldScroll);
    this.selectedEl.classList.toggle('is-scrollable', shouldScroll);

    const selectedMarkup = this.selected.map((item) => `
      <article class="tp-byob-selected">
        <div class="tp-byob-selected__image">
          ${item.image ? `<img src="${this.escape(item.image)}" alt="${this.escape(item.productTitle)}" loading="lazy" width="160" height="160">` : ''}
        </div>
        <button class="tp-byob-selected__remove" type="button" data-remove-selected="${this.escape(item.uid)}" aria-label="${this.escape(this.strings.remove)} ${this.escape(item.productTitle)}">×</button>
        <span class="tp-byob-selected__title">${this.escape(item.productTitle)}</span>
        <span class="tp-byob-selected__variant">${this.escape(item.variantTitle)}</span>
      </article>
    `).join('');

    const emptyCount = shouldScroll ? 0 : Math.max(0, 3 - this.selected.length);
    const emptyMarkup = Array.from({ length: emptyCount }).map(() => '<div class="tp-byob-slot">+</div>').join('');
    this.selectedEl.innerHTML = `${selectedMarkup}${emptyMarkup}`;
    this.emptySlotsEl.innerHTML = '';

    if (!this.maxItems || this.selected.length < this.maxItems) {
      this.limitMessageEl.hidden = true;
    }

    const minimumHint = this.selected.length < this.minItems
      ? `<span class="tp-byob__subtotal-hint">${this.escape(this.strings.minimumHint || `${this.minItems} items minimum`)}</span>`
      : '';

    this.subtotalEl.innerHTML = discount > 0
      ? `<span class="tp-byob__subtotal-price"><span class="tp-byob__subtotal-compare">${this.formatGoalMoney(total)}</span><span class="tp-byob__subtotal-sale">${this.formatGoalMoney(finalTotal)}</span><span class="tp-byob__subtotal-badge">-${discountPercent}%</span></span>`
      : `<span class="tp-byob__subtotal-price">${this.formatGoalMoney(total)}</span>`;
    this.subtotalEl.insertAdjacentHTML('afterbegin', minimumHint);

    this.addBundleButton.disabled = this.adding || this.selected.length < this.minItems;
    this.addBundleButton.textContent = this.adding ? this.strings.addingBundle : this.strings.addBundle;
    this.updateScrollIndicator();
  }

  updateScrollIndicator() {
    if (!this.scrollIndicator || !this.selectedEl) return;
    const isScrollable = this.selectedWrapEl?.classList.contains('is-scrollable');
    const max = this.selectedEl.scrollWidth - this.selectedEl.clientWidth;
    const percent = max > 0 ? this.selectedEl.scrollLeft / max : 0;
    const track = this.selectedEl.clientWidth * 0.66;
    this.scrollIndicator.style.setProperty('--scroll-x', `${Math.max(0, track * percent)}px`);
    if (this.selectedPrevButton) this.selectedPrevButton.hidden = !isScrollable || this.selectedEl.scrollLeft <= 2;
    if (this.selectedNextButton) this.selectedNextButton.hidden = !isScrollable || this.selectedEl.scrollLeft >= max - 2;
  }

  scrollSelectedItems(direction) {
    if (!this.selectedEl) return;
    const distance = this.selectedEl.clientWidth * 0.72;
    this.selectedEl.scrollBy({ left: distance * direction, behavior: 'smooth' });
  }

  async addBundleToCart() {
    if (this.adding || this.selected.length < this.minItems) return;
    this.adding = true;
    this.renderSummary();

    const bundleId = `tp-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const bundleDiscountPercent = this.discountPercent;
    const grouped = this.selected.reduce((map, item) => {
      const key = String(item.variantId);
      map[key] = map[key] || { item, quantity: 0 };
      map[key].quantity += 1;
      return map;
    }, {});

    const items = Object.values(grouped).map(({ item, quantity }) => ({
      id: item.variantId,
      quantity,
      properties: {
        _tp_bundle_id: bundleId,
        _tp_bundle_title: this.dataset.bundleTitle || 'Build your bundle',
        _tp_bundle_image: this.dataset.bundleImage || '',
        _tp_bundle_discount_percent: String(bundleDiscountPercent),
        _tp_bundle_item_title: item.productTitle,
        _tp_bundle_item_variant: item.variantTitle,
      },
    }));

    try {
      const sectionsToBundle = [];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );
      const drawer = document.querySelector('cart-drawer');

      const body = JSON.stringify({
        items,
        sections: sectionsToBundle,
        section_url: window.location.pathname,
        sections_url: window.location.pathname,
      });
      const response = await fetch(
        window.FoxTheme?.routes?.cart_add_url || '/cart/add.js',
        window.FoxTheme?.utils?.fetchConfig
          ? { ...window.FoxTheme.utils.fetchConfig('javascript'), body }
          : {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
              body,
            }
      );
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.description || 'Failed to add bundle');

      const cartResponse = await fetch(window.FoxTheme?.routes?.cart_url || '/cart.js', {
        ...(window.FoxTheme?.utils?.fetchConfig ? window.FoxTheme.utils.fetchConfig() : {}),
      });
      const cart = await cartResponse.json();
      cart.sections = added.sections || {};

      if (window.FoxTheme?.pubsub && window.FoxTheme?.pubsub?.PUB_SUB_EVENTS?.cartUpdate) {
        window.FoxTheme.pubsub.publish(window.FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          cart,
        });
      }

      this.selected = [];
      this.clearSelectedStorage();
      document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: added } }));
      if (drawer && typeof drawer.show === 'function') {
        drawer.show(this.addBundleButton);
      }

      this.summaryEl.classList.remove('is-expanded');
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.toggleText.textContent = this.strings.expand;
    } catch (error) {
      console.error(error);
    } finally {
      this.adding = false;
      this.renderSummary();
    }
  }

  escape(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}

if (!customElements.get('timephoria-bundle-builder')) {
  customElements.define('timephoria-bundle-builder', TimephoriaBundleBuilder);
}
